import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/fmp_landing.css';
import '../styles/solicitacao_admissao.css';
import logoComece from '../assets/logo-comece.svg';

// Página PÚBLICA (sem login) — par do /admissao, para desligamentos.
// O RH/gestor do cliente preenche e a solicitação cai em
// solicitacao_desligamento, aparecendo na página de Rescisões do BPO RH.
//
// O colaborador é digitado, não escolhido numa lista: a página é aberta e
// publicar o quadro de funcionários de cada empresa nela seria expor dado
// pessoal a qualquer um com o link.

const FORM_INICIAL = {
  email_solicitante: '',
  empresa: '',
  cnpj: '',
  colaborador: '',
  tipo_desligamento: '',
  categoria_desligamento: '',
  aviso_previo: '',
  data_inicio_aviso: '',
  data_final_desligamento: '',
  desvincular_beneficios: '',
  data_remocao_acesso: '',
  motivo_desligamento: '',
  website: '', // honeypot anti-bot — humano não vê nem preenche
};

const mascararCnpj = (v) => {
  const d = String(v || '').replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

// Definido FORA do componente: identidade estável entre renders
// (dentro, o React remontaria o input a cada tecla e o foco se perderia).
const Campo = ({ id, rotulo, icone, obrigatorio, full, children }) => (
  <div className={`sa-field ${full ? 'full' : ''}`}>
    <label htmlFor={id}>
      {icone && <i className={`fas ${icone}`} aria-hidden="true"></i>}
      {rotulo} {obrigatorio && <span className="req">*</span>}
    </label>
    {children}
  </div>
);

// Grupo de opções em botões — evita o radio miúdo do print da Convenia
const Escolha = ({ opcoes, valor, onChange, nome }) => (
  <div className="sd-escolhas" role="radiogroup" aria-label={nome}>
    {opcoes.map((o) => (
      <button
        key={o}
        type="button"
        role="radio"
        aria-checked={valor === o}
        className={`sd-escolha ${valor === o ? 'ativa' : ''}`}
        onClick={() => onChange(o)}
      >
        {valor === o && <i className="fas fa-check" aria-hidden="true"></i>}
        {o}
      </button>
    ))}
  </div>
);

const SolicitacaoDesligamento = () => {
  const [form, setForm] = useState(FORM_INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState('');
  const [opcoes, setOpcoes] = useState({
    tipos_desligamento: [], avisos_previos: [], categorias: [],
  });

  // Empresa identificada pelo CNPJ — sem isso a solicitação é recusada pelo
  // backend (era possível lançar uma rescisão digitando um nome qualquer)
  const [empresaCnpj, setEmpresaCnpj] = useState('');
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [erroCnpj, setErroCnpj] = useState('');

  const digitosCnpj = form.cnpj.replace(/\D/g, '');

  // As listas vêm do backend para formulário e validação nunca divergirem
  useEffect(() => {
    let cancelado = false;
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/desligamento/opcoes/`)
      .then((res) => {
        if (cancelado) return;
        setOpcoes({
          tipos_desligamento: res.data?.tipos_desligamento || [],
          avisos_previos: res.data?.avisos_previos || [],
          categorias: res.data?.categorias || [],
        });
      })
      .catch(() => {
        if (!cancelado) setErro('Não foi possível carregar as opções do formulário. Recarregue a página.');
      });
    return () => { cancelado = true; };
  }, []);

  // Confirma a empresa pelo CNPJ (o endpoint devolve só o nome dela)
  useEffect(() => {
    if (digitosCnpj.length !== 14) {
      setEmpresaCnpj('');
      setErroCnpj('');
      return;
    }
    let cancelado = false;
    setBuscandoCnpj(true);
    setErroCnpj('');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/desligamento/opcoes/`, { params: { cnpj: digitosCnpj } })
      .then((res) => {
        if (cancelado) return;
        setEmpresaCnpj(res.data?.empresa || '');
        // o nome da empresa passa a vir do cadastro, não da digitação
        setForm((f) => ({ ...f, empresa: res.data?.empresa || f.empresa }));
      })
      .catch((err) => {
        if (cancelado) return;
        setEmpresaCnpj('');
        setErroCnpj(err.response?.data?.mensagem
          || 'Não foi possível validar o CNPJ. Tente novamente.');
      })
      .finally(() => { if (!cancelado) setBuscandoCnpj(false); });
    return () => { cancelado = true; };
  }, [digitosCnpj]);

  const setCampo = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const setValor = (campo) => (valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const setCnpj = (e) => setForm((f) => ({ ...f, cnpj: mascararCnpj(e.target.value) }));

  const enviar = async (e) => {
    e.preventDefault();
    setErro('');

    // conferência de coerência das datas antes de gastar o envio
    const { data_inicio_aviso: ini, data_final_desligamento: fim } = form;
    if (ini && fim && fim < ini) {
      setErro('A data final do desligamento não pode ser anterior ao início do aviso prévio.');
      return;
    }
    if (!form.aviso_previo) {
      setErro('Selecione o tipo de aviso prévio.');
      return;
    }
    if (!form.desvincular_beneficios) {
      setErro('Informe se os benefícios devem ser desvinculados.');
      return;
    }
    // sem empresa identificada o backend recusa — barra aqui para não perder o
    // formulário preenchido
    if (!empresaCnpj) {
      setErro(digitosCnpj.length === 14
        ? 'Não identificamos esse CNPJ. Confira o número ou fale com a Comece Hub.'
        : 'Informe o CNPJ da empresa para continuar.');
      return;
    }

    setEnviando(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/solicitacao-desligamento/`,
        form
      );
      if (res.data?.success) {
        setEnviado(true);
      } else {
        setErro(res.data?.mensagem || 'Não foi possível enviar. Tente novamente.');
      }
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Não foi possível enviar. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fmp-site">
      {/* ── Topo ── */}
      <header className="sa-hero">
        <div className="sa-wrap">
          <img src={logoComece} alt="Comece Hub" style={{ height: 34, display: 'block', margin: '0 auto 6px' }} />
          <h1>Solicitação de Desligamento</h1>
          <p>
            Informe os dados do desligamento. A equipe de <b style={{ color: '#fff' }}>BPO de RH da
            Comece Hub</b> recebe a solicitação na hora e conduz a rescisão.
          </p>
        </div>
      </header>

      <div className="sa-wrap">
        <div className="sa-card">
          {enviado ? (
            <div className="sa-sucesso">
              <div className="ic"><i className="fas fa-check"></i></div>
              <h2>Solicitação enviada!</h2>
              <p>
                Recebemos os dados do desligamento de <b>{form.colaborador}</b>. Nossa equipe de RH
                vai conduzir o processo e entrará em contato se precisar de algo.
              </p>
              <button
                className="sa-btn"
                onClick={() => { setForm(FORM_INICIAL); setEnviado(false); }}
              >
                <i className="fas fa-plus"></i> Enviar outro desligamento
              </button>
            </div>
          ) : (
            <form onSubmit={enviar}>
              <h2>
                <i className="fas fa-user-minus" style={{ color: 'var(--cyan)', marginRight: 10 }}></i>
                Dados do desligamento
              </h2>
              <div className="sa-sub">
                Campos com <span style={{ color: 'var(--danger)' }}>*</span> são obrigatórios.
              </div>

              {erro && (
                <div className="sa-erro">
                  <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>{erro}
                </div>
              )}

              {/* honeypot anti-bot */}
              <div className="sa-hp" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" type="text" tabIndex={-1} autoComplete="off"
                  value={form.website} onChange={setCampo('website')} />
              </div>

              <div className="sa-grid">
                <Campo id="email_solicitante" rotulo="Seu e-mail" icone="fa-envelope" obrigatorio>
                  <input id="email_solicitante" type="email" required placeholder="voce@empresa.com.br"
                    value={form.email_solicitante} onChange={setCampo('email_solicitante')} />
                </Campo>

                <Campo id="cnpj" rotulo="CNPJ da empresa" icone="fa-id-card" obrigatorio>
                  <input id="cnpj" type="text" required placeholder="00.000.000/0000-00"
                    inputMode="numeric" value={form.cnpj} onChange={setCnpj} />
                  {buscandoCnpj && (
                    <span className="sa-cnpj-status">
                      <i className="fas fa-circle-notch fa-spin"></i> Buscando a empresa...
                    </span>
                  )}
                  {!buscandoCnpj && empresaCnpj && (
                    <span className="sa-cnpj-status ok">
                      <i className="fas fa-circle-check"></i> {empresaCnpj}
                    </span>
                  )}
                  {!buscandoCnpj && erroCnpj && (
                    <span className="sa-cnpj-status erro">
                      <i className="fas fa-circle-exclamation"></i> {erroCnpj}
                    </span>
                  )}
                </Campo>

                <Campo id="empresa" rotulo="Nome da empresa" icone="fa-building" obrigatorio>
                  <input id="empresa" type="text" required placeholder="Informe o CNPJ acima"
                    value={form.empresa} onChange={setCampo('empresa')} />
                </Campo>

                <Campo id="colaborador" rotulo="Nome completo do colaborador" icone="fa-user" obrigatorio full>
                  <input id="colaborador" type="text" required placeholder="Nome e sobrenome"
                    value={form.colaborador} onChange={setCampo('colaborador')} />
                </Campo>

                <Campo id="tipo_desligamento" rotulo="Tipo de desligamento" icone="fa-file-signature" obrigatorio full>
                  <select id="tipo_desligamento" required
                    value={form.tipo_desligamento} onChange={setCampo('tipo_desligamento')}>
                    <option value="">Selecione o tipo de desligamento...</option>
                    {opcoes.tipos_desligamento.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Campo>

                <Campo id="categoria_desligamento" rotulo="Categoria de desligamento" icone="fa-tags" full>
                  <Escolha
                    nome="Categoria de desligamento"
                    opcoes={opcoes.categorias}
                    valor={form.categoria_desligamento}
                    onChange={setValor('categoria_desligamento')}
                  />
                </Campo>

                <Campo id="aviso_previo" rotulo="Aviso prévio" icone="fa-bullhorn" obrigatorio full>
                  <Escolha
                    nome="Aviso prévio"
                    opcoes={opcoes.avisos_previos}
                    valor={form.aviso_previo}
                    onChange={setValor('aviso_previo')}
                  />
                </Campo>

                <Campo id="data_inicio_aviso" rotulo="Data do início do aviso prévio" icone="fa-calendar-day" obrigatorio>
                  <input id="data_inicio_aviso" type="date" required
                    value={form.data_inicio_aviso} onChange={setCampo('data_inicio_aviso')} />
                </Campo>

                <Campo id="data_final_desligamento" rotulo="Data final do desligamento" icone="fa-calendar-xmark" obrigatorio>
                  <input id="data_final_desligamento" type="date" required
                    min={form.data_inicio_aviso || undefined}
                    value={form.data_final_desligamento} onChange={setCampo('data_final_desligamento')} />
                </Campo>

                <Campo id="desvincular_beneficios" rotulo="Desvincular benefícios" icone="fa-heart-circle-minus" obrigatorio>
                  <Escolha
                    nome="Desvincular benefícios"
                    opcoes={['Sim', 'Não']}
                    valor={form.desvincular_beneficios}
                    onChange={setValor('desvincular_beneficios')}
                  />
                </Campo>

                <Campo id="data_remocao_acesso" rotulo="Quando remover o acesso à plataforma" icone="fa-user-lock" obrigatorio>
                  <input id="data_remocao_acesso" type="date" required
                    value={form.data_remocao_acesso} onChange={setCampo('data_remocao_acesso')} />
                </Campo>

                <div className="sa-field full">
                  <label htmlFor="motivo_desligamento">
                    <i className="fas fa-comment-dots" aria-hidden="true"></i>
                    Motivo do desligamento
                  </label>
                  <textarea id="motivo_desligamento"
                    placeholder="Contexto do desligamento e observações para a equipe de RH."
                    value={form.motivo_desligamento} onChange={setCampo('motivo_desligamento')} />
                  <span className="sd-dica">
                    <i className="fas fa-circle-info" aria-hidden="true"></i>
                    Não inclua informações de saúde, atestados ou CID — se houver, trate direto
                    com a equipe de RH.
                  </span>
                </div>
              </div>

              <div className="sa-actions">
                <button type="submit" className="sa-btn" disabled={enviando}>
                  {enviando
                    ? (<><i className="fas fa-circle-notch fa-spin"></i> Enviando...</>)
                    : (<>Enviar solicitação <i className="fas fa-paper-plane"></i></>)}
                </button>
                <span className="sa-note">
                  <i className="fas fa-lock" style={{ marginRight: 6 }}></i>
                  Os dados são usados exclusivamente para o processo de desligamento.
                </span>
              </div>
            </form>
          )}
        </div>
      </div>

      <footer className="sa-footer">
        <b>Comece Hub</b> · BPO de RH — Contabilidade consultiva · BPO Financeiro · BI
      </footer>
    </div>
  );
};

export default SolicitacaoDesligamento;
